import React from 'react';
import { FeatureCard } from '@components/ui/cards/FeatureCard';
import type { OnboardingStatus } from '@ts/types';

import { useOnboardingTasks } from '../../OnboardingTasksCard/hooks/useOnboardingTasks';
import { TaskExplanationModal } from '../../OnboardingTasksCard/modals/TaskExplanationModal';

import { OnboardingFooter } from './OnboardingFooter';
import { OnboardingTasks } from './OnboardingTasks';

export interface OnboardingCardProps {
  onboardingStatus?: OnboardingStatus;
}

/**
 * OnboardingCard Component
 *
 * A modern onboarding checklist card for Dashboard V2.
 * Orchestrates the task list, footer, and modals using the shared business logic.
 */
export const OnboardingCard: React.FC<OnboardingCardProps> = ({
  onboardingStatus: providedStatus,
}) => {
  const {
    tasks,
    onboardingStatus,
    isLoading,
    showOnboarding,
    userMetrics,
    completedTasks,
    totalTasks,
    modalState,
    handleOpenModal,
    handleCloseModal,
  } = useOnboardingTasks({ providedStatus });

  // Don't render if we shouldn't show onboarding
  if (!showOnboarding) {
    return null;
  }

  return (
    <>
      <FeatureCard
        title="Getting Started Checklist"
        fullHeight
        loading={isLoading}
        footer={
          <OnboardingFooter
            completedTasks={completedTasks}
            totalTasks={totalTasks}
            discountAmount={onboardingStatus?.discount_amount}
            applyDiscount={onboardingStatus?.apply_discount}
          />
        }
        contentSx={{
          p: 3,
        }}
      >
        <OnboardingTasks tasks={tasks} userMetrics={userMetrics} onTaskClick={handleOpenModal} />
      </FeatureCard>

      {/* Modals */}
      {modalState.isOpen &&
        modalState.task &&
        modalState.task.task_id !== 'consent_information' && (
          <TaskExplanationModal
            open={modalState.isOpen}
            task={modalState.task}
            onClose={handleCloseModal}
          />
        )}
    </>
  );
};

export default OnboardingCard;
